import { fetchEventSource, FetchEventSourceInit } from '@microsoft/fetch-event-source';

import { getCurrentUserToken } from '../../../auth/authService';
import getClientConfig from '../../getClientConfig';

const clientConfig = getClientConfig();
const apiRootUrl = clientConfig.apiRoot || '';
interface FetchEventSourceInitWithUrl extends FetchEventSourceInit {
    url: string;
    data?: { [key: string]: unknown };
    apiRoot?: string;
}

/*
    Extends default fetchEventSource to add authorization header & always expect JSON response.
*/

/**
 * @param {string} url The url to fetch. Will be prefixed with /api/
 * @param {string} method The HTTP method to use
 * @param headers The headers to use. Authorization header will be added automatically.
 * @param data Request body. Will be stringified to JSON.
 * @param onopen Callback for when the connection is opened
 * @param onmessage Callback for when a message is received
 * @param onerror Callback for when an error occurs
 * @param onclose Callback for when the connection is closed
 * @returns {Promise<void>}
 * @description Extends default fetchEventSource to add authorization header & always expect JSON response.
 *
 */
export default ({ url, method, headers, apiRoot, onerror, ...rest }: FetchEventSourceInitWithUrl): Promise<void> => {
    const headersWithAuth = {
        Authorization: `Bearer ${getCurrentUserToken()}`,
        'Content-Type': 'application/json',
        ...headers,
    };

    const apiUrl = apiRoot ? apiRoot : `${apiRootUrl}/api`;

    return fetchEventSource(`${apiUrl}/${url}`, {
        method: method || 'POST',
        headers: headersWithAuth,
        body: rest.body || JSON.stringify(rest.data),
        onerror:
            onerror &&
            ((error) => {
                // By default, fetchEventSource will auto-retry if onerror returns undefined
                // while returning a number explicitly retries after that duration.
                // Rethrowing indicates "don't retry" -- which we want to do always, and don't
                // want to risk unwanted retries from someone forgetting to rethrow.
                // So we wrap it here to rethrow by default.
                const returnval = onerror(error);
                if (returnval === undefined) {
                    throw error;
                }
                return returnval;
            }),
        ...rest,
    });
};
